import React, { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/lang";
import { useContent } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback options if CMS is empty. Options are language-agnostic (used as keys on the backend).
const OPTION_SETS = {
  profile: ["Incorporadora", "Loteadora", "Construtora", "Imobiliária", "Parceiro"],
  development_type: ["Vertical Alto Padrão", "Vertical Médio", "Horizontal", "Loteamento", "Multipropriedade", "Corporate"],
  phase: ["Produto", "Branding", "Lançamento", "Sustentação", "Estoque", "Crise", "Performance"],
};

export default function ConciergeAI() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [textInput, setTextInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [contact, setContact] = useState({ contact_name: "", contact_email: "", contact_phone: "" });
  const [contactSent, setContactSent] = useState(false);
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { concierge_questions: CMS_QS } = useContent();
  const C = t.concierge || {};
  const Q = C.questions || {};

  // Prefer CMS questions when present, otherwise fall back to i18n + hard-coded option sets.
  // Every step MUST end with a "whatsapp" input step (enforced by ConciergeEditor).
  const STEPS = useMemo(() => {
    if (Array.isArray(CMS_QS) && CMS_QS.length > 0) {
      return CMS_QS.map((s) => ({
        key: s.key,
        q: (s.q && s.q[lang]) || (s.q && s.q.pt) || s.key,
        input: !!s.input,
        placeholder: (s.placeholder && s.placeholder[lang]) || (s.placeholder && s.placeholder.pt) || "",
        options: Array.isArray(s.options) && s.options.length ? s.options : undefined,
      }));
    }
    // Legacy fallback
    return [
      { key: "profile", q: Q.profile || "Você é incorporadora, loteadora, construtora, imobiliária ou parceiro?", options: OPTION_SETS.profile },
      { key: "development_type", q: Q.devType || "Qual tipo de empreendimento?", options: OPTION_SETS.development_type },
      { key: "phase", q: Q.phase || "Em qual fase está hoje?", options: OPTION_SETS.phase },
      { key: "challenge", q: Q.challenge || "Qual o maior desafio agora?", input: true, placeholder: "..." },
      { key: "location", q: Q.location || "Em qual cidade/estado?", input: true, placeholder: "Ex.: Goiânia/GO" },
      { key: "whatsapp", q: "Seu WhatsApp", input: true, placeholder: "Ex.: (62) 9 9999-9999" },
    ];
  }, [CMS_QS, lang, Q]);

  const reset = () => { setStep(0); setAnswers({}); setResult(null); setTextInput(""); setContact({ contact_name: "", contact_email: "", contact_phone: "" }); setContactSent(false); };

  const submitAnswer = async (value) => {
    const current = STEPS[step];
    const updated = { ...answers, [current.key]: value };
    setAnswers(updated);
    setTextInput("");
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        // Map special "whatsapp" step to the backend contact_phone field.
        const payload = { ...updated };
        if (payload.whatsapp && !payload.contact_phone) {
          payload.contact_phone = payload.whatsapp;
        }
        const { data } = await axios.post(`${API}/ai/concierge`, payload);
        setResult(data);
      } catch (e) {
        setResult({ services: [], cta: "Vamos conversar.", reasoning: "Não conseguimos processar agora — entre em contato direto.", caseTags: [], insightTopics: [] });
      } finally {
        setLoading(false);
      }
    }
  };

  const sendContact = async () => {
    if (!contact.contact_email && !contact.contact_phone) return;
    try {
      // Re-post with contact info to register/update the lead with contact
      await axios.post(`${API}/ai/concierge`, { ...answers, ...contact });
      setContactSent(true);
    } catch (e) {
      setContactSent(true); // Don't block the user
    }
  };

  const current = STEPS[step];

  return (
    <>
      <button
        onClick={() => { setOpen(true); reset(); }}
        data-testid="concierge-trigger"
        aria-label="Mordomo Places — IA"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-black text-white rounded-full pl-3 pr-5 py-3 flex items-center gap-2 shadow-2xl border border-white/10 hover:scale-105 transition-transform"
      >
        <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4FF00] opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4FF00]"></span></span>
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">{C.title || "Mordomo Places"}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-end md:items-center justify-end md:pr-8 md:pb-8" data-testid="concierge-panel">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ duration: 0.3 }} className="relative w-full md:max-w-md theme-dark m-0 md:m-0 p-6 md:p-8 md:rounded-2xl border border-white/10 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-accent-yellow" /><span className="font-display text-lg">{C.title || "Mordomo Places"}</span></div>
                <button onClick={() => setOpen(false)} data-testid="concierge-close"><X className="w-5 h-5" /></button>
              </div>

              {!result && !loading && (
                <div>
                  <div className="text-xs text-neutral-500 mb-2">{step + 1}/{STEPS.length}</div>
                  <div className="font-display text-2xl leading-tight mb-6">{current.q}</div>
                  {current.options ? (
                    <div className="flex flex-wrap gap-2">
                      {current.options.map((opt) => (
                        <button key={opt} onClick={() => submitAnswer(opt)} data-testid={`concierge-opt-${opt}`} className="px-4 py-2 text-sm border border-white/20 rounded-full hover:bg-accent-yellow hover:text-black hover:border-transparent transition">{opt}</button>
                      ))}
                    </div>
                  ) : (
                    <form onSubmit={(e) => { e.preventDefault(); if (textInput.trim()) submitAnswer(textInput.trim()); }}>
                      <input value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder={current.placeholder} data-testid="concierge-input" className="w-full bg-transparent border border-white/20 rounded-full px-4 py-3 text-sm outline-none focus:border-accent-yellow" />
                      <button type="submit" data-testid="concierge-submit" className="mt-3 bg-accent-yellow text-black px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2"><Send className="w-4 h-4" /> {t.cta?.send || "Enviar"}</button>
                    </form>
                  )}
                </div>
              )}

              {loading && (<div className="py-12 flex flex-col items-center gap-4 text-neutral-400"><Loader2 className="w-8 h-8 animate-spin text-accent-yellow" /><span className="text-sm">…</span></div>)}

              {result && (
                <div data-testid="concierge-result">
                  <div className="text-xs text-accent-yellow uppercase tracking-widest mb-2">{C.resultHeader || "Recomendação do Mordomo"}</div>
                  <div className="font-display text-2xl leading-tight mb-4">{result.cta}</div>
                  <p className="text-sm text-neutral-400 mb-6">{result.reasoning}</p>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">{C.suggestedServices || "Serviços sugeridos"}</div>
                  <ul className="space-y-2 mb-6">
                    {result.services?.map((s) => (<li key={s} className="text-sm border-l-2 border-accent-green pl-3 py-1">{s}</li>))}
                  </ul>

                  {!contactSent ? (
                    <div className="mb-6 border border-white/10 rounded-lg p-4 bg-white/[0.02]">
                      <div className="text-xs uppercase tracking-widest text-accent-yellow mb-2">{C.contactPrompt || "Quer que a gente entre em contato?"}</div>
                      <p className="text-xs text-neutral-400 mb-3">{C.contactSub || "Deixe seu contato e um especialista da Places retorna em até 24h."}</p>
                      <div className="space-y-2">
                        <input value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} placeholder="—" className="w-full bg-transparent border border-white/15 rounded-full px-3 py-2 text-xs outline-none focus:border-accent-yellow" data-testid="concierge-contact-name" />
                        <input value={contact.contact_email} onChange={(e) => setContact({ ...contact, contact_email: e.target.value })} placeholder="E-mail" type="email" className="w-full bg-transparent border border-white/15 rounded-full px-3 py-2 text-xs outline-none focus:border-accent-yellow" data-testid="concierge-contact-email" />
                        <input value={contact.contact_phone} onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })} placeholder="WhatsApp" className="w-full bg-transparent border border-white/15 rounded-full px-3 py-2 text-xs outline-none focus:border-accent-yellow" data-testid="concierge-contact-phone" />
                        <button onClick={sendContact} disabled={!contact.contact_email && !contact.contact_phone} data-testid="concierge-contact-submit" className="w-full bg-accent-yellow text-black px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-40">{C.contactSubmit || "Quero ser contatado"}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 border border-accent-green/30 bg-accent-green/10 rounded-lg p-4 text-xs text-accent-green" data-testid="concierge-contact-sent">
                      {C.contactSent || "Recebemos seu contato. Em breve falaremos com você. ✓"}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <button onClick={() => { setOpen(false); navigate("/contato?intent=client"); }} data-testid="concierge-cta-contact" className="bg-accent-yellow text-black px-5 py-3 rounded-full text-sm font-semibold">{C.talkPlaces || "Falar com a Places"}</button>
                    <button onClick={() => { setOpen(false); navigate("/cases"); }} className="border border-white/20 px-5 py-3 rounded-full text-sm">{C.seeCases || "Ver cases relacionados"}</button>
                    <button onClick={reset} className="text-xs text-neutral-500 mt-2">{C.restart || "Refazer perguntas"}</button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
